import { useState, useEffect, useCallback, useRef } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import { debounce } from "lodash";
import axios from "axios";
import instance from "../../../axios/instance";

const CompoundDrugAutocomplete = ({ input, handleInputChange }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const resultsCache = useRef({});
  const cancelTokenRef = useRef(null);

  const getDrugsByName = useCallback(async (query) => {
    try {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("New request made");
      }

      // Check cache first
      if (resultsCache.current[query]) {
        setOptions(resultsCache.current[query]);
        setLoading(false);
        return;
      }

      // Create new cancel token
      cancelTokenRef.current = axios.CancelToken.source();

      setLoading(true);
      const response = await instance.get(
        `/drugs/formulations?query=${encodeURIComponent(query)}`,
        {
          cancelToken: cancelTokenRef.current.token,
        }
      );

      // Store in cache
      resultsCache.current[query] = response.data;
      setOptions(response.data);
    } catch (error) {
      if (!axios.isCancel(error)) {
        console.error("Error fetching drugs:", error);
        setOptions([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce((query) => {
      if (query.length > 2) {
        getDrugsByName(query);
      } else {
        setOptions([]);
        setLoading(false);
      }
    }, 400),
    [getDrugsByName]
  );

  // Handle input change
  const handleDrugInput = (event, value) => {
    setInputValue(value);
    debouncedSearch(value);
  };

  // Clear options when component unmounts
  useEffect(() => {
    return () => {
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
    };
  }, []);

  return (
    <div>
      <Autocomplete
        id="drug-autocomplete"
        disablePortal={false}
        freeSolo
        value={input}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        filterOptions={(x) => x} // Disable client-side filtering
        options={options}
        loading={loading}
        inputValue={inputValue}
        onInputChange={handleDrugInput}
        getOptionLabel={(option) => {
          return option.name || "";
        }}
        onChange={(e, val) => handleInputChange(val)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Type ingredient name or select"
            required={true}
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        renderOption={(props, option) => {
          const { key, ...rest } = props; // ⚠️  pull key out
          return (
            <li
              key={option.id}
              {...rest}
              className="px-4 py-2 hover:bg-gray-100"
            >
              <div>
                <div className="font-medium">{option.name}</div>
                <div className="text-sm text-gray-600">{option.cas}</div>
              </div>
            </li>
          );
        }}
      />
    </div>
  );
};

export default CompoundDrugAutocomplete;
